// Player-to-player TRADE window (+ the incoming-invite prompt). The server is authoritative: this
// just stages MY offer and mirrors the live state it sends back. Top = their offer, bottom = mine;
// click bag items to add, click offered items to remove, type cash. Lock → (both locked) Confirm →
// done. Cancel anytime. The window only actually closes when the SERVER says the trade ended.

import { ITEM_NAMES, NON_STACKABLE_ITEMS, type TradeUpdateMessage, type TradeItem, type ItemMods } from "@ghostland/shared";
import { inventoryStore } from "../items/inventoryStore.js";
import { balanceStore } from "../items/balanceStore.js";
import { itemThumb } from "../items/itemThumb.js";
import { injectBillStyle, cashIcon } from "./pixelBills.js";
import { injectTheme } from "./theme.js";

export interface TradeActions {
  offer: (items: TradeItem[], cash: number) => void;
  lock: (locked: boolean) => void;
  confirm: () => void;
  cancel: () => void;
  respond: (accept: boolean) => void;
}
let actions: TradeActions | null = null;
export function setTradeActions(a: TradeActions): void { actions = a; }

const STYLE_ID = "ghostland-trade-style";
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Silkscreen:wght@400;700&display=swap');
#gl-trade-overlay, #gl-trade-invite {
  position: fixed; inset: 0; z-index: 34; display: none; align-items: center; justify-content: center;
  background: rgba(0,0,0,.7); color: #e8e8e8;
}
#gl-trade-overlay.open, #gl-trade-invite.open { display: flex; }
#gl-trade-overlay *, #gl-trade-invite * { font-family: 'Silkscreen', ui-monospace, monospace !important; }
#gl-trade-card { width: 360px; padding: 18px; background: #000; border: 2px solid #f4f4f4; image-rendering: pixelated; }
#gl-trade-card h1 { margin: 0 0 12px; font-size: 11px; letter-spacing: 2px; color: #f4f4f4; }
.gl-trade-side { position: relative; overflow: hidden; border: 1px solid #2a2a2a; padding: 8px; margin-bottom: 8px; }
/* tint that covers a side once it's LOCKED (amber) or CONFIRMED (green) */
.gl-trade-fx { position: absolute; inset: 0; pointer-events: none; background: transparent; transition: background .15s; }
.gl-trade-fx.lock { background: rgba(230,181,58,.20); box-shadow: inset 0 0 0 2px rgba(230,181,58,.5); }
.gl-trade-fx.ok { background: rgba(121,209,138,.24); box-shadow: inset 0 0 0 2px rgba(121,209,138,.6); }
.gl-trade-side h2 { margin: 0 0 6px; font-size: 8px; letter-spacing: 1px; color: #b0b0b0; display: flex; justify-content: space-between; }
.gl-trade-badge { font-size: 7px; letter-spacing: 1px; }
.gl-trade-badge.lock { color: #e6b53a; } .gl-trade-badge.ok { color: #79d18a; } .gl-trade-badge.pick { color: #6a6a6a; }
.gl-trade-grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 4px; min-height: 34px; }
.gl-trade-cell {
  width: 100%; aspect-ratio: 1; background: #0c0c0c; border: 1px solid #2a2a2a; position: relative;
  display: flex; align-items: flex-end; justify-content: flex-end;
}
.gl-trade-cell.click { cursor: pointer; } .gl-trade-cell.click:hover { border-color: #f4f4f4; }
.gl-trade-cell.dim { opacity: .3; }
.gl-trade-cell canvas { position: absolute; inset: 0; width: 100%; height: 100%; image-rendering: pixelated; }
.gl-trade-cell .q { position: relative; font-size: 8px; color: #f4f4f4; padding: 0 2px 1px; text-shadow: 1px 1px 0 #000; }
.gl-trade-cash { display: flex; align-items: center; gap: 6px; margin-top: 6px; font-size: 9px; color: #d8d8d8; }
.gl-trade-cash input {
  width: 90px; background: #0c0c0c; border: 1px solid #3a3a3a; color: #f4f4f4; font-size: 9px; padding: 3px 5px;
  font-family: inherit;
}
#gl-trade-bag h2 { margin: 0 0 6px; font-size: 8px; letter-spacing: 1px; color: #8a8a8a; }
#gl-trade-btns { display: flex; gap: 8px; margin-top: 10px; }
.gl-trade-btn { flex: 1; padding: 8px 0; background: #000; border: 2px solid #6a6a6a; color: #b0b0b0; cursor: pointer; font-size: 8px; letter-spacing: 1px; image-rendering: pixelated; }
.gl-trade-btn:hover:not(:disabled) { background: #f4f4f4; color: #000; border-color: #f4f4f4; }
.gl-trade-btn:disabled { opacity: .35; cursor: default; }
.gl-trade-btn.go { border-color: #79d18a; color: #79d18a; }
.gl-trade-btn.go:hover:not(:disabled) { background: #79d18a; color: #000; }
.gl-trade-btn.cancel { border-color: #b46a6a; color: #c89090; }
.gl-trade-btn.cancel:hover { background: #b46a6a; color: #000; }
#gl-trade-invite-card { width: 280px; padding: 20px; background: #000; border: 2px solid #f4f4f4; text-align: center; }
#gl-trade-invite-card p { font-size: 10px; color: #e8e8e8; margin: 0 0 16px; line-height: 1.5; }
`;

function ensureStyle(): void {
  injectTheme(); injectBillStyle();
  if (!document.getElementById(STYLE_ID)) { const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s); }
}

// --- incoming invite prompt ---
let inviteEl: HTMLDivElement | null = null;
export function showTradeInvite(fromName: string): void {
  ensureStyle();
  if (!inviteEl) {
    inviteEl = document.createElement("div");
    inviteEl.id = "gl-trade-invite";
    inviteEl.innerHTML = `<div id="gl-trade-invite-card"><p></p><div style="display:flex;gap:8px;"><button class="gl-trade-btn go" id="gl-ti-acc">Accept</button><button class="gl-trade-btn cancel" id="gl-ti-dec">Decline</button></div></div>`;
    document.body.appendChild(inviteEl);
    inviteEl.querySelector("#gl-ti-acc")!.addEventListener("click", () => { actions?.respond(true); hideInvite(); });
    inviteEl.querySelector("#gl-ti-dec")!.addEventListener("click", () => { actions?.respond(false); hideInvite(); });
  }
  inviteEl.querySelector("p")!.textContent = `${fromName} wants to trade.`;
  inviteEl.classList.add("open");
}
function hideInvite(): void { inviteEl?.classList.remove("open"); }
/** Hide the invite prompt (e.g. on Esc — the caller sends the decline). */
export function closeTradeInvite(): void { hideInvite(); }

// --- trade window ---
let overlay: HTMLDivElement | null = null;
let titleEl: HTMLElement | null = null;
let theirGrid: HTMLDivElement | null = null, theirCashEl: HTMLDivElement | null = null, theirBadge: HTMLElement | null = null, theirFx: HTMLDivElement | null = null;
let myGrid: HTMLDivElement | null = null, myCashInput: HTMLInputElement | null = null, myBadge: HTMLElement | null = null, myFx: HTMLDivElement | null = null;
let bagGrid: HTMLDivElement | null = null;
let primaryBtn: HTMLButtonElement | null = null;
let unsub: Array<() => void> = [];

// Keyed `s<slotIndex>` for a unique item offered from a specific bag slot, or `i<itemId>` for a
// stackable aggregate — so two armored pieces of the same id but different rolls stay distinct and
// each carries its own mods.
let myItems = new Map<string, TradeItem>();
let myCash = 0;
let last: TradeUpdateMessage | null = null;

const offerKey = (it: { itemId: string; slot?: number }): string => (it.slot != null ? `s${it.slot}` : `i${it.itemId}`);

function pushOffer(): void {
  actions?.offer([...myItems.values()], myCash);
}

function cell(itemId: string, qty: number, onClick: (() => void) | null, dim = false, mods?: ItemMods): HTMLDivElement {
  const c = document.createElement("div");
  c.className = "gl-trade-cell" + (onClick ? " click" : "") + (dim ? " dim" : "");
  const name = ITEM_NAMES[itemId as keyof typeof ITEM_NAMES] ?? itemId;
  c.title = mods ? `${name} (STR+${mods.strength} DEX+${mods.dex} AGI+${mods.agility})` : name;
  c.appendChild(itemThumb(itemId));
  if (qty > 1) { const q = document.createElement("span"); q.className = "q"; q.textContent = String(qty); c.appendChild(q); }
  if (onClick) c.addEventListener("click", onClick);
  return c;
}

function ensure(): void {
  ensureStyle();
  if (overlay) return;
  overlay = document.createElement("div");
  overlay.id = "gl-trade-overlay";
  overlay.innerHTML = `
    <div id="gl-trade-card">
      <h1></h1>
      <div class="gl-trade-side"><h2><span id="gl-t-their-name">THEIR OFFER</span><span class="gl-trade-badge" id="gl-t-their-badge"></span></h2>
        <div class="gl-trade-grid" id="gl-t-their"></div><div class="gl-trade-cash" id="gl-t-their-cash"></div>
        <div class="gl-trade-fx" id="gl-t-their-fx"></div></div>
      <div class="gl-trade-side"><h2><span>YOUR OFFER</span><span class="gl-trade-badge" id="gl-t-my-badge"></span></h2>
        <div class="gl-trade-grid" id="gl-t-my"></div>
        <div class="gl-trade-cash">${cashIcon()} <input id="gl-t-my-cash" type="number" min="0" step="1" value="0" /></div>
        <div class="gl-trade-fx" id="gl-t-my-fx"></div></div>
      <div id="gl-trade-bag"><h2>YOUR BAG — CLICK TO ADD</h2><div class="gl-trade-grid" id="gl-t-bag"></div></div>
      <div id="gl-trade-btns"><button class="gl-trade-btn" id="gl-t-primary"></button><button class="gl-trade-btn cancel" id="gl-t-cancel">Cancel</button></div>
    </div>`;
  document.body.appendChild(overlay);
  titleEl = overlay.querySelector("h1");
  theirGrid = overlay.querySelector("#gl-t-their"); theirCashEl = overlay.querySelector("#gl-t-their-cash"); theirBadge = overlay.querySelector("#gl-t-their-badge"); theirFx = overlay.querySelector("#gl-t-their-fx");
  myGrid = overlay.querySelector("#gl-t-my"); myCashInput = overlay.querySelector("#gl-t-my-cash"); myBadge = overlay.querySelector("#gl-t-my-badge"); myFx = overlay.querySelector("#gl-t-my-fx");
  bagGrid = overlay.querySelector("#gl-t-bag");
  primaryBtn = overlay.querySelector("#gl-t-primary");
  overlay.querySelector("#gl-t-cancel")!.addEventListener("click", () => actions?.cancel());
  myCashInput!.addEventListener("change", () => {
    if (last?.myLock) return;
    myCash = Math.max(0, Math.min(Math.floor(Number(myCashInput!.value) || 0), balanceStore.get()));
    myCashInput!.value = String(myCash); pushOffer();
  });
}

function badge(el: HTMLElement | null, lock: boolean, confirm: boolean): void {
  if (!el) return;
  el.textContent = confirm ? "CONFIRMED ✓" : lock ? "LOCKED" : "PICKING…";
  el.className = "gl-trade-badge " + (confirm ? "ok" : lock ? "lock" : "pick");
}

/** Tint a side: green once confirmed, amber once locked, clear otherwise. */
function sideFx(el: HTMLDivElement | null, lock: boolean, confirm: boolean): void {
  if (el) el.className = "gl-trade-fx" + (confirm ? " ok" : lock ? " lock" : "");
}

function render(): void {
  if (!last || !overlay) return;
  const u = last, inv = inventoryStore.get(), bal = balanceStore.get();
  if (titleEl) titleEl.textContent = `TRADE WITH ${u.theirName.toUpperCase()}`;
  // their offer (read-only; show rolled mods in the tooltip)
  theirGrid!.replaceChildren(...u.theirs.items.map((it) => cell(it.itemId, it.qty, null, false, it.mods)));
  theirCashEl!.innerHTML = `${cashIcon()} <b>${u.theirs.cash.toLocaleString()}</b>`;
  badge(theirBadge, u.theirLock, u.theirConfirm);
  sideFx(theirFx, u.theirLock, u.theirConfirm);
  // my offer (click to remove, unless locked)
  myGrid!.replaceChildren(...[...myItems].map(([key, it]) => cell(it.itemId, it.qty, u.myLock ? null : () => {
    if (it.slot != null) myItems.delete(key);                              // unique: drop the whole piece
    else { const n = it.qty - 1; if (n <= 0) myItems.delete(key); else it.qty = n; } // stackable: one unit
    pushOffer();
  }, false, it.mods)));
  if (myCashInput && document.activeElement !== myCashInput) myCashInput.value = String(myCash);
  if (myCashInput) myCashInput.disabled = !!u.myLock;
  badge(myBadge, u.myLock, u.myConfirm);
  sideFx(myFx, u.myLock, u.myConfirm);
  // my bag: stackables aggregate by item; unique pieces (armored/pet) get one cell PER SLOT so each
  // keeps its own roll. Click to add, unless locked / none left / already offered.
  const cells: HTMLDivElement[] = [];
  const ownedStack = new Map<string, number>();
  for (const s of inv) if (!NON_STACKABLE_ITEMS.has(s.itemId)) ownedStack.set(s.itemId, (ownedStack.get(s.itemId) ?? 0) + s.quantity);
  for (const [id, total] of ownedStack) {
    const avail = total - (myItems.get(`i${id}`)?.qty ?? 0);
    const can = avail > 0 && !u.myLock;
    cells.push(cell(id, avail, can ? () => {
      const e = myItems.get(`i${id}`); if (e) e.qty += 1; else myItems.set(`i${id}`, { itemId: id, qty: 1 }); pushOffer();
    } : null, avail <= 0));
  }
  for (const s of inv) {
    if (!NON_STACKABLE_ITEMS.has(s.itemId)) continue;
    const key = `s${s.slotIndex}`;
    const offered = myItems.has(key);
    const can = !offered && !u.myLock;
    cells.push(cell(s.itemId, 1, can ? () => {
      myItems.set(key, { itemId: s.itemId, qty: 1, slot: s.slotIndex, mods: s.mods, pet: s.pet }); pushOffer();
    } : null, offered, s.mods));
  }
  bagGrid!.replaceChildren(...cells);
  // primary button
  const bothLocked = u.myLock && u.theirLock;
  const b = primaryBtn!;
  b.className = "gl-trade-btn";
  if (!u.myLock) { b.textContent = "Lock"; b.disabled = false; b.onclick = () => actions?.lock(true); }
  else if (!bothLocked) { b.textContent = "Unlock"; b.disabled = false; b.onclick = () => actions?.lock(false); }
  else if (!u.myConfirm) { b.className = "gl-trade-btn go"; b.textContent = "Confirm"; b.disabled = false; b.onclick = () => actions?.confirm(); }
  else { b.textContent = "Waiting…"; b.disabled = true; b.onclick = null; }
  void bal;
}

/** Open / update the trade window from a server state push. */
export function renderTrade(u: TradeUpdateMessage): void {
  ensure();
  hideInvite();
  myItems = new Map(u.mine.items.map((i) => [offerKey(i), { ...i }])); // server is authoritative for my side
  myCash = u.mine.cash;
  last = u;
  if (!overlay!.classList.contains("open")) {
    overlay!.classList.add("open");
    unsub = [inventoryStore.subscribe(render), balanceStore.subscribe(render)];
  }
  render();
}

/** Close the window (called when the SERVER reports the trade ended). */
export function closeTradeWindow(): void {
  overlay?.classList.remove("open");
  hideInvite();
  unsub.forEach((fn) => fn()); unsub = [];
  myItems = new Map<string, TradeItem>(); myCash = 0; last = null;
}

export function isTradeOpen(): boolean { return overlay?.classList.contains("open") ?? false; }
export function isTradeInviteOpen(): boolean { return inviteEl?.classList.contains("open") ?? false; }
